import dayjs from 'dayjs';
import z from 'zod';

export const receivingScopeSchema = z.enum(['PROJECT', 'SHOP']);

export const receivingLineSchema = z.object({
  item_id: z.coerce.number().int().positive().optional(),
  part_id: z.coerce.number().int().positive().optional(),
  item_code: z.string().trim().max(100).optional().default(''),
  item_name: z.string().trim().max(255).optional().default(''),
  description: z.string().trim().max(500).optional().default(''),
  category: z.string().trim().max(100).optional().default(''),
  unit_of_measure: z.string().trim().max(30).optional().default('each'),
  expected_qty: z.coerce
    .number()
    .int({ error: 'Quantity must be a whole number.' })
    .positive({ error: 'Enter a quantity greater than zero.' }),
  received_qty: z.coerce
    .number()
    .int({ error: 'Received quantity must be a whole number.' })
    .min(1, { error: 'Received quantity must be at least one.' })
    .optional(),
  reference: z.string().trim().max(255).optional().default(''),
  notes: z.string().trim().max(500).optional().default(''),
  supplier_item_number: z.string().trim().max(255).optional().default(''),
});

export const receivingBatchSchema = z
  .object({
    scope: receivingScopeSchema,
    project_id: z.coerce.number().int().positive().nullable().optional(),
    supplier_id: z.coerce.number().int().positive().nullable().optional(),
    reference: z.string().trim().max(255).optional().default(''),
    packing_slip_number: z.string().trim().max(255).optional().default(''),
    expected_date: z
      .string()
      .min(1, { error: 'Choose an expected delivery date.' })
      .refine((value) => dayjs(value).isValid(), {
        error: 'Choose a valid expected delivery date.',
      }),
    notes: z.string().trim().max(1000).optional().default(''),
    receive_now: z.boolean().default(false),
    lines: z
      .array(receivingLineSchema)
      .min(1, { error: 'Add at least one item.' }),
  })
  .superRefine((batch, context) => {
    if (batch.scope === 'PROJECT' && !batch.project_id) {
      context.addIssue({
        code: 'custom',
        path: ['project_id'],
        message: 'Select a project before adding project parts.',
      });
    }

    const identities = new Set<string>();

    batch.lines.forEach((line, index) => {
      if (batch.scope === 'PROJECT' && !line.item_id && !line.part_id) {
        if (!line.item_code || !line.item_name) {
          context.addIssue({
            code: 'custom',
            path: ['lines', index, 'item_code'],
            message:
              'Search for a project part or enter both a manual item code and name.',
          });
        }
      }

      if (
        batch.scope === 'SHOP' &&
        !line.item_id &&
        (!line.item_code || !line.item_name)
      ) {
        context.addIssue({
          code: 'custom',
          path: ['lines', index, 'item_code'],
          message: 'Enter both an item code and item name.',
        });
      }

      if (
        batch.receive_now &&
        line.received_qty != null &&
        line.received_qty > line.expected_qty
      ) {
        context.addIssue({
          code: 'custom',
          path: ['lines', index, 'received_qty'],
          message: 'Received quantity cannot exceed expected quantity.',
        });
      }

      const identity = line.item_id
        ? `id:${line.item_id}`
        : line.part_id
          ? `part:${line.part_id}`
          : `code:${line.item_code
              .trim()
              .toUpperCase()
              .replace(/[\s_-]+/g, '')}`;

      if (identities.has(identity)) {
        context.addIssue({
          code: 'custom',
          path: [
            'lines',
            index,
            line.item_id ? 'item_id' : line.part_id ? 'part_id' : 'item_code',
          ],
          message: 'This item appears more than once.',
        });
      }
      identities.add(identity);
    });
  });

export const receivingImportRowSchema = receivingLineSchema.extend({
  row_number: z.coerce.number().int().positive(),
  include: z.boolean().default(true),
  confidence: z.coerce.number().min(0).max(1).nullable().optional(),
  warnings: z.array(z.string()).optional().default([]),
  errors: z.array(z.string()).optional().default([]),
  low_confidence: z.boolean().optional(),
  matched_part_id: z.coerce.number().int().positive().nullable().optional(),
  confirmed: z.boolean().optional(),
  confirm_create_item: z.boolean().optional(),
});

export const receivingImportCommitSchema = z
  .object({
    import_id: z.coerce.number().int().positive(),
    scope: receivingScopeSchema,
    project_id: z.coerce.number().int().positive().nullable().optional(),
    supplier_id: z.coerce.number().int().positive().nullable().optional(),
    reference: z.string().trim().max(255).optional().default(''),
    expected_date: z
      .string()
      .min(1, { error: 'Choose an expected delivery date.' }),
    notes: z.string().trim().max(1000).optional().default(''),
    receive_now: z.boolean().default(false),
    rows: z.array(receivingImportRowSchema),
  })
  .superRefine((data, context) => {
    if (!data.rows.some((row) => row.include)) {
      context.addIssue({
        code: 'custom',
        path: ['rows'],
        message: 'Select at least one row to import.',
      });
    }

    if (data.scope === 'PROJECT' && !data.project_id) {
      context.addIssue({
        code: 'custom',
        path: ['project_id'],
        message: 'Select a project before importing project parts.',
      });
    }

    data.rows.forEach((row, index) => {
      if (!row.include) return;
      if (row.errors.length > 0) {
        context.addIssue({
          code: 'custom',
          path: ['rows', index, 'errors'],
          message: 'Correct this row or remove it from the import.',
        });
      }
      if (row.low_confidence && row.confirmed !== true) {
        context.addIssue({
          code: 'custom',
          path: ['rows', index, 'confirmed'],
          message: 'Confirm that you reviewed this low-confidence row.',
        });
      }
      if (
        !row.item_id &&
        !row.matched_part_id &&
        row.confirm_create_item !== true
      ) {
        context.addIssue({
          code: 'custom',
          path: ['rows', index, 'confirm_create_item'],
          message: 'Confirm creation of this new inventory item.',
        });
      }
      if (
        data.receive_now &&
        row.received_qty != null &&
        row.received_qty > row.expected_qty
      ) {
        context.addIssue({
          code: 'custom',
          path: ['rows', index, 'received_qty'],
          message: 'Received quantity cannot exceed expected quantity.',
        });
      }
    });
  });

export type ReceivingBatchFormValues = z.infer<typeof receivingBatchSchema>;
export type ReceivingImportCommitValues = z.infer<
  typeof receivingImportCommitSchema
>;
